/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useDispatch } from 'react-redux';
import { Message } from '@arco-design/web-react';
import logger from './logger';
import {
  setHistoryMsg,
  setInterruptMsg,
  updateAITalkState,
  updateAIThinkState,
} from '@/store/slices/room';
import RtcClient from '@/lib/RtcClient';
import { string2tlv, tlv2String } from '@/utils/utils';

export type AnyRecord = Record<string, any>;

export enum MESSAGE_TYPE {
  BRIEF = 'conv',
  SUBTITLE = 'subv',
  FUNCTION_CALL = 'tool',
}

export enum AGENT_BRIEF {
  UNKNOWN,
  LISTENING,
  THINKING,
  SPEAKING,
  INTERRUPTED,
  FINISHED,
}

/**
 * @brief 指令类型
 */
export enum COMMAND {
  /**
   * @brief 打断指令
   */
  INTERRUPT = 'interrupt',
  /**
   * @brief 控制 AI 音频播放
   */
  AUDIO_CTRL = 'AudioCtrl',
  /**
   * @brief 发送外部文本驱动 TTS
   */
  EXTERNAL_TEXT_TO_SPEECH = 'ExternalTextToSpeech',
  /**
   * @brief 发送外部文本驱动 LLM
   */
  EXTERNAL_TEXT_TO_LLM = 'ExternalTextToLLM',
}
/**
 * @brief 打断的类型
 */
export enum INTERRUPT_PRIORITY {
  /**
   * @brief 占位
   */
  NONE,
  /**
   * @brief 高优先级。传入信息直接打断交互，进行处理。
   */
  HIGH,
  /**
   * @brief 中优先级。等待当前交互结束后，进行处理。
   */
  MEDIUM,
  /**
   * @brief 低优先级。如当前正在发生交互，直接丢弃 Message 传入的信息。
   */
  LOW,
}

export const MessageTypeCode = {
  [MESSAGE_TYPE.SUBTITLE]: 1,
  [MESSAGE_TYPE.FUNCTION_CALL]: 2,
  [MESSAGE_TYPE.BRIEF]: 3,
};

let lastBriefToast = '';
let lastBriefConsoleKey = '';
// #region debug-point B:debug-reporter
const DEBUG_SERVER_URL = 'http://127.0.0.1:7777/event';
const DEBUG_SESSION_ID = 'image-link-tts';
const LATENCY_DEBUG_SESSION_ID = 'search-latency-breakdown';
const isDebugReporterEnabled = () =>
  process.env.REACT_APP_DEBUG_REPORTER === 'true' ||
  (typeof window !== 'undefined' && (window as any).__VOXIE_DEBUG_REPORTER__ === true);
const reportDebugEvent = (
  hypothesisId: string,
  location: string,
  msg: string,
  data: Record<string, unknown> = {},
  runId = 'pre-fix'
) => {
  if (!isDebugReporterEnabled()) return;
  fetch(DEBUG_SERVER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId: DEBUG_SESSION_ID,
      runId,
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => {});
};

const reportLatencyDebugEvent = (
  hypothesisId: string,
  location: string,
  msg: string,
  data: Record<string, unknown> = {},
  runId = 'pre-fix'
) => {
  if (!isDebugReporterEnabled()) return;
  fetch(DEBUG_SERVER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId: LATENCY_DEBUG_SESSION_ID,
      runId,
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => {});
};

const getActiveSearchLatencyContext = () => {
  if (typeof window === 'undefined') return null;
  const activeRequest = (
    window as Window & {
      __searchLatencyActiveRequest?: {
        requestKey?: string;
        dispatchAt?: number;
        requestText?: string;
        source?: string;
      };
    }
  ).__searchLatencyActiveRequest;
  if (!activeRequest?.requestKey || !Number.isFinite(activeRequest.dispatchAt)) return null;
  return {
    requestKey: String(activeRequest.requestKey),
    dispatchAt: Number(activeRequest.dispatchAt),
    requestText: String(activeRequest.requestText || ''),
    source: String(activeRequest.source || ''),
  };
};
// #endregion

const collectHints = (obj: any, depth = 0, out: string[] = []) => {
  if (!obj || depth > 4) return out;
  const t = typeof obj;
  if (t === 'string') {
    const s = obj.trim();
    if (s && s !== 'errorOccurred') out.push(s);
    return out;
  }
  if (t === 'number' || t === 'boolean') {
    out.push(String(obj));
    return out;
  }
  if (Array.isArray(obj)) {
    obj.slice(0, 8).forEach((it) => collectHints(it, depth + 1, out));
    return out;
  }
  if (t === 'object') {
    const keys = Object.keys(obj);
    // Prefer common error fields first.
    const preferred = [
      'Message',
      'message',
      'Reason',
      'reason',
      'Detail',
      'detail',
      'Error',
      'error',
      'Code',
      'code',
      'Status',
      'status',
    ];
    preferred.forEach((k) => {
      if (k in obj) collectHints(obj[k], depth + 1, out);
    });
    keys
      .filter((k) => !preferred.includes(k))
      .slice(0, 12)
      .forEach((k) => collectHints(obj[k], depth + 1, out));
  }
  return out;
};

const stringifyBriefPayload = (payload: unknown) => {
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
};

const shouldSuppressKnownAigcBriefToast = (payload: unknown) => {
  const text = stringifyBriefPayload(payload);
  return (
    text.includes('preParamCheck') &&
    text.includes('ProviderParams.Additions') &&
    (text.includes('enable_latex_tn') || text.includes('disable_markdown_filter'))
  );
};

export const useMessageHandler = () => {
  const dispatch = useDispatch();

  const maps = {
    /**
     * @brief 接收状态变化信息
     * @note https://www.volcengine.com/docs/6348/1415216?s=g
     */
    [MESSAGE_TYPE.BRIEF]: (parsed: AnyRecord) => {
      const { Stage } = parsed || {};
      const { Code, Description } = Stage || {};
      logger.debug('[MESSAGE_TYPE.BRIEF]: ', Code, Description);

      // Surface possible async errors (LLM/TTS/ASR) that happen after StartVoiceChat returns OK.
      // Avoid spamming by deduping identical messages.
      if (Description && typeof Description === 'string') {
        const normalized = `${Code}:${Description}`;
        const looksLikeError =
          /error|fail|invalid|denied|forbidden|unauth|timeout/i.test(Description) ||
          /失败|错误|无权限|超时|异常|非法/.test(Description);
        if (
          looksLikeError &&
          normalized !== lastBriefToast &&
          !shouldSuppressKnownAigcBriefToast(parsed)
        ) {
          lastBriefToast = normalized;
          Message.error(`AI 侧异常：${Description}（打开控制台看详情）`);
        }
      }

      // "errorOccurred" is too generic; dump the full payload for debugging.
      // Expose it on window as well for quick copy/paste.
      if (Description === 'errorOccurred') {
        const key = stringifyBriefPayload(parsed || Stage || {});
        if (key !== lastBriefConsoleKey) {
          lastBriefConsoleKey = key;
          const briefText = stringifyBriefPayload(parsed || Stage || {});
          try {
            (window as any).__last_agent_brief = parsed;
            (window as any).__last_agent_brief_text = briefText;
          } catch {
            // ignore
          }
          // Make it very visible in console.
          // eslint-disable-next-line no-console
          console.error('[AIGC BRIEF ERROR]', parsed);
          // eslint-disable-next-line no-console
          console.error('[AIGC BRIEF ERROR JSON]\n', briefText);

          if (shouldSuppressKnownAigcBriefToast(parsed)) {
            return;
          }

          // Try to extract more actionable info for non-technical users.
          const hints = collectHints(parsed || Stage || {})
            .filter((s) => s.length <= 180)
            .filter((s) => !/^\d+$/.test(s));
          const uniq: string[] = [];
          hints.forEach((h) => {
            if (!uniq.includes(h)) uniq.push(h);
          });
          if (uniq.length) {
            Message.error(`AI 侧异常：${uniq.slice(0, 3).join(' | ')}`);
          } else {
            Message.error(
              'AI 侧异常：errorOccurred（请打开控制台复制 window.__last_agent_brief_text）'
            );
          }
        }
      }

      switch (Code) {
        case AGENT_BRIEF.THINKING:
          {
            const activeRequest = getActiveSearchLatencyContext();
            // #region debug-point L:brief-thinking
            reportLatencyDebugEvent(
              'L',
              'src/utils/handler.ts:BRIEF',
              '[LATENCY] Agent brief thinking observed',
              {
                code: Code,
                description: Description || '',
                requestKey: activeRequest?.requestKey || '',
                requestText: activeRequest?.requestText || '',
                source: activeRequest?.source || '',
                elapsedFromDispatchMs: activeRequest ? Date.now() - activeRequest.dispatchAt : null,
              }
            );
            // #endregion
          }
          dispatch(updateAIThinkState({ isAIThinking: true }));
          break;
        case AGENT_BRIEF.SPEAKING:
          // #region debug-point B:brief-speaking
          reportDebugEvent('B', 'src/utils/handler.ts:BRIEF', '[DEBUG] Agent brief speaking', {
            code: Code,
            description: Description || '',
          });
          // #endregion
          dispatch(updateAITalkState({ isAITalking: true }));
          break;
        case AGENT_BRIEF.FINISHED:
          // #region debug-point B:brief-finished
          reportDebugEvent('B', 'src/utils/handler.ts:BRIEF', '[DEBUG] Agent brief finished', {
            code: Code,
            description: Description || '',
          });
          // #endregion
          dispatch(updateAITalkState({ isAITalking: false }));
          break;
        case AGENT_BRIEF.INTERRUPTED:
          dispatch(setInterruptMsg());
          break;
        default:
          break;
      }
    },
    /**
     * @brief 字幕
     * @note https://www.volcengine.com/docs/6348/1337284?s=g
     */
    [MESSAGE_TYPE.SUBTITLE]: (parsed: AnyRecord) => {
      const data = parsed.data?.[0] || {};
      /** debounce 记录用户输入文字 */
      if (data) {
        const { text: msg, definite, userId: user, paragraph } = data;
        if (!msg) return;
        const isAudioEnable = RtcClient.getAgentEnabled();
        if ((window as any)._debug_mode) {
          logger.debug('handleRoomBinaryMessageReceived', data);
        }
        if (isAudioEnable) {
          // #region debug-point C:subtitle-chunk
          reportDebugEvent(
            'C',
            'src/utils/handler.ts:SUBTITLE',
            '[DEBUG] Subtitle chunk received',
            {
              user,
              paragraph: Boolean(paragraph),
              definite: Boolean(definite),
              text: String(msg).slice(0, 500),
              textLength: String(msg).length,
            }
          );
          // #endregion
          dispatch(setHistoryMsg({ text: msg, user, paragraph, definite }));
        }
      }
    },
    /**
     * @brief Function calling
     * @note https://www.volcengine.com/docs/6348/1359441?s=g
     */
    [MESSAGE_TYPE.FUNCTION_CALL]: (parsed: AnyRecord) => {
      const name: string = parsed?.tool_calls?.[0]?.function?.name;
      const toolCallId = parsed?.tool_calls?.[0]?.id || '';
      const rawArguments = parsed?.tool_calls?.[0]?.function?.arguments || '';
      const activeRequest = getActiveSearchLatencyContext();
      // #region debug-point M:function-call
      reportLatencyDebugEvent(
        'M',
        'src/utils/handler.ts:FUNCTION_CALL',
        '[LATENCY] Function/tool call observed',
        {
          requestKey: activeRequest?.requestKey || '',
          requestText: activeRequest?.requestText || '',
          source: activeRequest?.source || '',
          elapsedFromDispatchMs: activeRequest ? Date.now() - activeRequest.dispatchAt : null,
          toolName: name || '',
          toolCallId,
          argumentsPreview: String(rawArguments || '').slice(0, 300),
        }
      );
      // #endregion
      console.log('[Function Call] - Called by sendUserBinaryMessage');
      const map: Record<string, string> = {
        getcurrentweather: '今天下雪， 最低气温零下10度',
      };

      RtcClient.engine.sendUserBinaryMessage(
        'RobotMan_',
        string2tlv(
          JSON.stringify({
            ToolCallID: parsed?.tool_calls?.[0]?.id,
            Content: map[name.toLocaleLowerCase().replaceAll('_', '')],
          }),
          'func'
        )
      );
    },
  };

  return {
    parser: (buffer: ArrayBuffer) => {
      try {
        const { type, value } = tlv2String(buffer);
        maps[type as MESSAGE_TYPE]?.(JSON.parse(value));
      } catch (e) {
        logger.debug('parse error', e);
      }
    },
  };
};
